<i18n lang="json">
{
  "zh-cn": {
    "title": "资金明细趋势",
    "recent_1_week": "近1周",
    "recent_1_month": "近1月",
    "recent_1_year": "近1年",
    "recharge":"充值",
    "transfer_in":"转入"
  },
  "en": {
    "title": "Fund Details Trend",
    "recent_1_week": "Last 7 Days",
    "recent_1_month": "Last 30 Days",
    "recent_1_year": "Last 365 Days",
    "recharge":"Recharge",
    "transfer_in":"To transfer into"
  }
}
</i18n>

<script setup lang="ts">
import apiIndex from '@/api/modules/index'
import * as echarts from 'echarts'
import { useI18n } from 'vue-i18n'
import { toast } from 'vue-sonner'

const { t } = useI18n()
const loading = ref(false)
const activeName = ref<'year' | 'month' | 'week'>('week')
const xdata = ref<Array<string>>([])
const ydata = ref<Array<{
  i18n_key: string
  data: Array<string | number>
  color: string
}>>([])
function getData() {
  loading.value = true
  apiIndex.financialTrends({ type: activeName.value }).then((res) => {
    if (res.status === 0) {
      xdata.value = res.data.xdata
      ydata.value = res.data.ydata
    }
    else {
      toast.error(res.msg)
    }
  }).finally(() => {
    loading.value = false
    initChart()
  })
}

const chartRef = ref()
let chart: echarts.ECharts | null = null

function parseData(data: (number | string)[]) {
  return data.map(item => Number(item))
}

function initChart() {
  if (!chartRef.value) {
    return false
  }

  const option: echarts.EChartsOption = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
      },
      valueFormatter: value => `${value}`,
    },
    legend: {
      top: 8, // 距离顶部10px
      left: 'center', // 水平居中
      orient: 'horizontal', // 水平排列
      itemGap: 24, // 图例项间距
      itemWidth: 14,
      itemHeight: 14,
      textStyle: {
        fontSize: 12,
      },
    },
    grid: {
      top: 40, // 根据图例高度调整
      left: 0, // 减少左侧留白
      right: 0, // 对称减少右侧留白
      bottom: 0, // 减少底部留白
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: xdata.value,
      axisLabel: {
        color: '#666',
        margin: 8, // 调整标签间距
      },
      axisLine: {
        lineStyle: {
          color: '#ddd',
        },
      },
      axisTick: {
        show: false, // 隐藏刻度线
      },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        color: '#666',
        formatter: (value: number) => value.toFixed(2),
        margin: 8, // 调整标签间距
      },
      splitLine: {
        lineStyle: {
          type: 'dashed',
          color: '#eee', // 调浅分割线颜色
        },
      },
      axisTick: {
        show: false, // 隐藏刻度线
      },
    },
    series: ydata.value.map(item => ({
      name: t(item.i18n_key),
      type: 'line',
      smooth: true,
      symbol: 'circle',
      symbolSize: 8,
      itemStyle: {
        color: item.color,
      },
      lineStyle: {
        color: item.color,
        width: 2,
      },
      areaStyle: {
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: `${item.color}40` },
          { offset: 1, color: `${item.color}00` },
        ]),
      },
      data: parseData(item.data),
    })),
  }

  if (!chart) {
    chart = echarts.init(chartRef.value)
  }
  chart.setOption(option)
}

// 窗口自适应
const handleResize = () => chart?.resize()
onMounted(() => {
  getData()
  window.addEventListener('resize', handleResize)
})
onUnmounted(() => {
  chart?.dispose()
  window.removeEventListener('resize', handleResize)
})
</script>

<template>
  <FaPageMain
    v-loading="loading"
    class="m-0 mb-5 min-h-95 p-0 shadow-[0px_0px_16px_0px_rgba(0,0,0,0.03)]"
    main-class="p-4 flex-1 flex flex-col justify-between"
  >
    <p>
      <span class="font-size-5 c-[var(--el-color-primary-dark-6)] font-bold">{{ t('title') }}</span>

      <el-tabs v-model="activeName" class="float-right max-w-full lg:mr-5" @tab-change="getData">
        <el-tab-pane :label="t('recent_1_week')" name="week" />
        <el-tab-pane :label="t('recent_1_month')" name="month" />
        <el-tab-pane :label="t('recent_1_year')" name="year" />
      </el-tabs>
    </p>
    <div ref="chartRef" class="min-h-70 w-full" />
  </FaPageMain>
</template>

<style scoped>
.el-tabs {
  --el-tabs-header-height: 30px;

  :deep(.el-tabs__header) {
    margin-bottom: 0;
  }
}
</style>
